/**
 * Tool call and approval presentation (prompt.md §23, §25).
 *
 * WHY the arguments are shown verbatim: an approval gate is only meaningful if
 * the person approving can see exactly what will run. This component therefore
 * never summarizes, never truncates the values in the approval view, and never
 * hides the raw arguments behind a collapsed section by default.
 */

import { useState } from "react";
import type { Approval } from "@dayvise/domain";

import { Badge } from "../primitives/Feedback.tsx";
import { Button } from "../primitives/Button.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { describeApproval, approvalStatusLabel } from "../logic/approval.ts";
import { formatArguments, toolLabel } from "../logic/messageParts.ts";
import { formatRelativeTime } from "../logic/format.ts";
import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";
import type { StatusTone } from "../logic/runState.ts";

export interface ToolCallCardProps {
  readonly toolId: string;
  readonly arguments: unknown;
  readonly result?: unknown;
  readonly ok?: boolean;
  readonly status?: "running" | "completed" | "failed" | "awaiting_approval" | "delegated";
  readonly startedAt?: string;
  readonly finishedAt?: string;
}

const STATUS_LABELS: Readonly<Record<NonNullable<ToolCallCardProps["status"]>, string>> = {
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  awaiting_approval: "Awaiting approval",
  delegated: "Ran in the run container",
};

/**
 * One tool invocation in the transcript. Arguments and results are shown as
 * preformatted JSON so a user can copy them out and verify what happened.
 */
export function ToolCallCard({ toolId, arguments: args, result, ok, status = "completed", startedAt, finishedAt }: ToolCallCardProps) {
  const tone: StatusTone = status === "failed" ? "danger" : status === "awaiting_approval" ? "warning" : "neutral";
  const duration = startedAt && finishedAt ? Date.parse(finishedAt) - Date.parse(startedAt) : null;
  return (
    <Card
      title={toolLabel(toolId)}
      subtitle={[toolId, STATUS_LABELS[status], duration !== null && Number.isFinite(duration) ? `${duration}ms` : null].filter(Boolean).join(" · ")}
    >
      <div style={stackStyle("var(--dayvise-space-xs)")}>
        <Badge tone={tone}>{STATUS_LABELS[status]}</Badge>
        {args !== undefined && args !== null ? (
          <div>
            <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>Arguments</span>
            <pre style={PRE_STYLE}>{formatArguments(args, Number.MAX_SAFE_INTEGER)}</pre>
          </div>
        ) : null}
        {result !== undefined ? (
          <div>
            <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{ok === false ? "Error" : "Result"}</span>
            <pre style={PRE_STYLE}>{formatArguments(result, Number.MAX_SAFE_INTEGER)}</pre>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

const PRE_STYLE = {
  margin: 0,
  padding: "var(--dayvise-space-xs)",
  background: "var(--dayvise-color-canvas)",
  border: "1px solid var(--dayvise-color-border)",
  borderRadius: "var(--dayvise-radius-sm)",
  fontSize: "var(--dayvise-font-size-xs)",
  fontFamily: "var(--dayvise-font-family-mono)",
  overflowX: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
} as const;

export interface ApprovalPromptProps {
  readonly approval: Approval;
  readonly onApprove: (approval: Approval) => void | Promise<void>;
  readonly onReject: (approval: Approval) => void | Promise<void>;
  readonly busy?: boolean;
  /** Identity of the person deciding; shown so an agent can never self-approve. */
  readonly decidedByLabel?: string;
}

/**
 * The approval gate. Approve and Reject are equally prominent: a destructive
 * action must never be easier to accept than to decline.
 */
export function ApprovalPrompt({ approval, onApprove, onReject, busy = false, decidedByLabel }: ApprovalPromptProps) {
  const descriptor = describeApproval(approval);
  // `describeApproval` has always reported whether this risk level needs an explicit
  // confirmation; this component ignored it and rendered one primary Approve for every
  // level. So a critical, destructive action was accepted with the same single click as a
  // trivial one -- and with a more prominent button than the Reject beside it, which is the
  // opposite of what the doc comment above promises.
  const [armed, setArmed] = useState(false);

  if (approval.status !== "pending") {
    return (
      <Card title={descriptor.toolLabel} subtitle={`Decision: ${approvalStatusLabel(approval.status)}`}>
        <div style={stackStyle("var(--dayvise-space-xs)")}>
          <Badge tone={approval.status === "approved" ? "success" : "neutral"}>{approvalStatusLabel(approval.status)}</Badge>
          {approval.decidedAt ? <span style={MUTED_TEXT}>Decided {formatRelativeTime(approval.decidedAt)}</span> : null}
          {approval.note ? <span style={MUTED_TEXT}>Note: {approval.note}</span> : null}
          <pre style={PRE_STYLE}>{descriptor.argumentsText}</pre>
        </div>
      </Card>
    );
  }

  return (
    <Card title={descriptor.headline} subtitle={`Risk: ${descriptor.riskLabel}`}>
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        <Badge tone={descriptor.tone}>{descriptor.riskLabel}</Badge>

        <div>
          <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>The agent will run exactly this</span>
          <pre style={PRE_STYLE}>{descriptor.actionLine}</pre>
        </div>

        {descriptor.arguments.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--dayvise-font-size-sm)" }}>
            <tbody>
              {descriptor.arguments.map((row) => (
                <tr key={row.name}>
                  <th scope="row" style={{ textAlign: "left", verticalAlign: "top", padding: "var(--dayvise-space-xxs) var(--dayvise-space-sm) var(--dayvise-space-xxs) 0", color: "var(--dayvise-color-text-muted)", fontWeight: "var(--dayvise-font-weight-regular)" }}>
                    {row.name}
                  </th>
                  <td style={{ padding: "var(--dayvise-space-xxs) 0", wordBreak: "break-word" }}>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        <div style={rowStyle("var(--dayvise-space-xs)")}>
          {descriptor.requiresExplicitConfirmation && !armed ? (
            // The first step only, and nothing is decided by it. The ellipsis is the whole
            // signal that another click follows.
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => setArmed(true)}>
              Approve {descriptor.toolLabel}...
            </Button>
          ) : descriptor.requiresExplicitConfirmation ? (
            <Button variant="danger" size="sm" loading={busy} onClick={() => void onApprove(approval)}>
              Yes, run {descriptor.toolLabel}
            </Button>
          ) : (
            <Button variant="primary" size="sm" loading={busy} onClick={() => void onApprove(approval)}>
              Approve and run
            </Button>
          )}
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => void onReject(approval)}>
            {descriptor.requiresExplicitConfirmation && armed ? "Go back" : "Reject"}
          </Button>
        </div>
        <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
          {decidedByLabel ?? "You"} will be recorded as the decision maker. The agent cannot approve its own request.
        </span>
      </div>
    </Card>
  );
}

export interface ApprovalListProps {
  readonly approvals: readonly Approval[];
  readonly onApprove: (approval: Approval) => void | Promise<void>;
  readonly onReject: (approval: Approval) => void | Promise<void>;
  readonly busy?: boolean;
}

export function ApprovalList({ approvals, onApprove, onReject, busy = false }: ApprovalListProps) {
  const pending = approvals.filter((approval) => approval.status === "pending");
  if (pending.length === 0) return null;
  return (
    <div style={stackStyle("var(--dayvise-space-sm)")}>
      {pending.map((approval) => (
        <ApprovalPrompt key={approval.approvalId} approval={approval} onApprove={onApprove} onReject={onReject} busy={busy} />
      ))}
    </div>
  );
}
